import { DynamicPaths, TFunction } from '@shared/types/types'

/**
 * Function that returns page titles from dynamic routes matching regex patterns.
 * @param t - translation TFunction
 * @returns an array of objects with paths and translated titles as key value pairs.
 */
export const getPathTitlesDynamic = (t: TFunction): DynamicPaths[] => [
  // AdminPaths
  { pattern: /^\/staffing\/batch-add\/([^/]+)$/, title: t('Batch add users') },
  { pattern: /^\/staffing\/([^/]+)$/, title: t('Staff details') },
  { pattern: /^\/tenant\/security\/sso\/([^/]+)$/, title: t('SSO provider details') },
  { pattern: /^\/check-ins\/category\/([^/]+)\/audience$/, title: t('Edit audience') },
  { pattern: /^\/team\/([^/]+)$/, title: t('Team details') },
  { pattern: /^\/team\/([^/]+)\/automation$/, title: t('Configure team automation') },
  { pattern: /^\/team\/([^/]+)\/edit$/, title: t('Edit team') },
  { pattern: /^\/analytics\/insight\/([^/]+)$/, title: t('Insight details') },
  { pattern: /^\/tv\/watch\/([^/]+)$/, title: t('Watch Kudos TV') },
  { pattern: /^\/values\/([^/]+)\/edit$/, title: t('Edit value') },
  { pattern: /^\/points\/allocation\/configure\/([^/]+)$/, title: t('Edit points allocation') },
  { pattern: /^\/points\/birthdays\/configure\/([^/]+)$/, title: t('Edit birthday gifts') },
  { pattern: /^\/points\/onboarding\/configure\/([^/]+)$/, title: t('Edit onboarding gifts') },
  { pattern: /^\/points\/reporting\/([^/]+)$/, title: t('Individual points reporting') },
  { pattern: /^\/rewards\/([^/]+)\/edit$/, title: t('Edit reward provider') },
  { pattern: /^\/rewards\/configure\/([^/]+)$/, title: t('Store settings') },
  { pattern: /^\/rewards\/store\/([^/]+)$/, title: t('My reward store') },
  { pattern: /^\/rewards\/store\/([^/]+)\/add-funds$/, title: t('Add funds') },
  { pattern: /^\/rewards\/store\/custom\/([^/]+)$/, title: t('Custom rewards') },
  { pattern: /^\/rewards\/configure\/custom\/([^/]+)$/, title: t('Store settings') },
  {
    pattern: /^\/rewards\/store\/custom\/([^/]+)\/reward\/configure$/,
    title: t('Add custom reward'),
  },
  {
    pattern: /^\/rewards\/store\/custom\/([^/]+)\/reward\/duplicate\/([^/]+)$/,
    title: t('Add custom reward'),
  },
  {
    pattern: /^\/rewards\/store\/custom\/([^/]+)\/reward\/configure\/([^/]+)$/,
    title: t('Edit custom reward'),
  },
  {
    pattern: /^\/rewards\/store\/custom\/([^/]+)\/reward\/([^/]+)\/pending$/,
    title: t('Pending fulfillments'),
  },
  {
    pattern: /^\/rewards\/store\/custom\/([^/]+)\/reward\/([^/]+)\/completed$/,
    title: t('Completed fulfillments'),
  },
  { pattern: /^\/points\/welcome\/configure\/([^/]+)$/, title: t('Edit welcome gifts') },
  { pattern: /^\/points\/anniversary\/configure\/([^/]+)$/, title: t('Edit anniversary gifts') },
  { pattern: /^\/points\/bank\/configure\/([^/]+)$/, title: t('Edit points bank') },
  { pattern: /^\/awards\/configure\/([^/]+)$/, title: t('Edit award') },
  { pattern: /^\/awards\/nomination\/([^/]+)$/, title: t('Nomination') },
  { pattern: /^\/awards\/award\/([^/]+)$/, title: t('Award') },
  { pattern: /^\/awards\/award\/bulk\/([^/]+)$/, title: t('Bulk send award') },
  { pattern: /^\/celebrate(?:\/)?(?:\?.*)?$/, title: t('Celebrations calendar') },
  { pattern: /^\/celebrate\/cards\/(all|available|own|received)(?:\?.*)?$/, title: t('Cards') },
  { pattern: /^\/celebrate\/card\/([^/]+)$/, title: t('Edit card') },
  { pattern: /^\/reports\/redemptionSummary$/, title: t('Redemptions') },
  { pattern: /^\/reports\/([^/]+)$/, title: t('Reports') },
  { pattern: /^\/reports\/([^/]+)\/share$/, title: t('Share report') },
  { pattern: /^\/reports\/([^/]+)\/share\/access$/, title: t('Share report') },
  { pattern: /^\/reports\/([^/]+)\/share\/email$/, title: t('Email sharing') },
  { pattern: /^\/reports\/([^/]+)\/share\/email\/scheduled$/, title: t('Scheduled email reports') },
  { pattern: /^\/reports\/([^/]+)\/share\/email\/past$/, title: t('Sent email reports') },
  { pattern: /^\/announcements\/configure\/([^/]+)$/, title: t('Edit announcement') },
  { pattern: /^\/announcements\/([^/]+)$/, title: t('Announcement') },
  { pattern: /^\/incentives\/([^/]+)$/, title: t('Incentive') },
  { pattern: /^\/incentives\/configure\/([^/]+)$/, title: t('Edit incentive') },
  { pattern: /^\/incentives\/duplicate\/([^/]+)$/, title: t('Create incentive') },
  { pattern: /^\/incentives\/manage\/([^/]+)\/pending$/, title: t('Pending claims') },
  { pattern: /^\/incentives\/manage\/([^/]+)\/past$/, title: t('Past claims') },
  { pattern: /^\/user\/([^/]+)\/points$/, title: t('Points summary') },
  { pattern: /^\/user\/([^/]+)\/details$/, title: t('User details') },
  { pattern: /^\/ftp\/([^/]+)\/imports$/, title: t('FTP connection summary') },
  { pattern: /^\/integrations\/ftp\/configure\/([^/]+)$/, title: t('Edit FTP connection') },
  { pattern: /^\/delegation\/([^/]+)$/, title: t('Delegation') },
  { pattern: /^\/delegation\/([^/]+)\/edit$/, title: t('Edit delegation') },
  { pattern: /^\/localization\/translations\/([^/]+)$/, title: t('Translations') },

  // TenantScopedPaths
  { pattern: /^\/check-ins\/category\/([^/]+)\/question$/, title: t('Check-in question') },
  { pattern: /^\/recognitions\/([^/]+)$/, title: t('Recognition') },
  { pattern: /^\/profile\/([^/]+)$/, title: t('Member profile') },
  { pattern: /^\/resource-hub\/resources\/([^/]+)$/, title: t('Resource hub') },
  { pattern: /^\/resource-hub\/resources\/([^/]+)\/complete$/, title: t('Resource complete') },
  { pattern: /^\/award\/([^/]+)$/, title: t('Award') },
  { pattern: /^\/awards\/award\/([^/]+)$/, title: t('Award details') },
  { pattern: /^\/card\/([^/]+)$/, title: t('Card') },
  { pattern: /^\/card\/([^/]+)(\/contributors)?$/, title: t('Card contributors') },
  { pattern: /^\/rewards\/tango\/([^/]+)$/, title: t('Reward') },
  { pattern: /^\/rewards\/([^/]+)\/([^/]+)$/, title: t('Rewards catalogue') },
  {
    pattern: /^\/rewards\/([^/]+)\/([^/]+)\/rewards\/([^/]+)$/,
    title: t('Redeem reward'),
  },
  { pattern: /^\/spaces\/configure\/([^/]+)$/, title: t('Edit space') },
  { pattern: /^\/spaces\/([^/]+)$/, title: t('Space') },
  { pattern: /^\/spaces\/([^/]+)\/albums$/, title: t('Albums') },
  { pattern: /^\/spaces\/([^/]+)\/albums\/([^/]+)$/, title: t('Album') },
  { pattern: /^\/spaces\/([^/]+)\/posts\/([^/]+)\/photos\/([^/]+)$/, title: t('Album photo') },
  { pattern: /^\/settings\/notification-category\/([^/]+)$/, title: t('Notification settings') },
]
